import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast'
import { useRef } from 'react'

import { ApiErrorCode } from '@/api/apiTypes'
import { AppButton } from '@/components'
import { BaseView } from '@/components/base'
import { TextInputForm } from '@/components/form'
import { inputLimits } from '@/constants/inputLimits'
import { useSendRecoveryCodeToEmail } from '@/domain'
import { IEmailSchema, useEmailSchema } from '@/schemas'
import { useAuthScopedStore } from '@/store/scoped'

export const RecoverPasswordWithEmailForm = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isSubmittingRef = useRef(false)

  const emailInIdentifyForm = useAuthScopedStore((state) => state.email)
  const setRecoverPasswordForm = useAuthScopedStore(
    (state) => state.setRecoverPasswordForm,
  )

  const emailSchema = useEmailSchema()

  const { handleSubmit, control, formState, trigger, setValue, setError } =
    useForm<IEmailSchema>({
      resolver: zodResolver(emailSchema),
      defaultValues: {
        email: emailInIdentifyForm || '',
      },
      mode: 'onBlur',
    })

  const { mutateAsync: sendRecoveryCodeToEmail, isPending } =
    useSendRecoveryCodeToEmail({
      onSuccess: ({ user_id }) => {
        setRecoverPasswordForm({ userId: user_id, recoveryMethod: 'email' })
        navigate('/auth/forgot-password/verification-code')
      },
      onError: ({ code, errorMessage }) => {
        if (!errorMessage) return

        if (code === ApiErrorCode.USER_NOT_FOUND) {
          setError('email', { message: errorMessage }, { shouldFocus: true })
          return
        }

        toast.error(errorMessage)
      },
    })

  const handleSubmitForm = async (formData: IEmailSchema) => {
    // Prevent multiple submissions using ref to avoid stale closure issues
    if (isSubmittingRef.current || isPending) {
      return
    }

    isSubmittingRef.current = true

    try {
      setRecoverPasswordForm({ email: formData.email })

      await sendRecoveryCodeToEmail({
        email: formData.email,
      })
    } finally {
      isSubmittingRef.current = false
    }
  }
  return (
    <>
      <TextInputForm
        control={control}
        name="email"
        label={t('translation:email')}
        placeholder={t('translation:emailPlaceholder')}
        maxLength={inputLimits.email.max}
        keyboardType="email-address"
        autoComplete="email"
        onChangeText={(value) => {
          setValue('email', value, { shouldValidate: false })
        }}
        onBlur={() => trigger('email')}
      />

      <BaseView mt="auto" mb={12}>
        <AppButton
          label={t('form:actions.confirm')}
          onPress={handleSubmit(handleSubmitForm)}
          disabled={!formState.isValid}
          isLoading={isPending}
        />
      </BaseView>
    </>
  )
}
